// RSO-change CAS-246 smart login
import React from "react";
import {Button, Divider, Flex, Form, Layout, Result, Spin, message} from "antd";
import {
  ServerUrl, getAcceptLanguage, goToLink, inIframe, isDarkTheme,
  isMobile, isProviderVisibleForSignIn, isProviderVisibleForSignUp, renderHelmet, renderLogo, showMessage
} from "../Setting";
import {GoogleOneTapLoginVirtualButton} from "./GoogleLoginButton";
import * as Provider from "./Provider";
import * as ProviderButton from "./ProviderButton";
import * as RsoAuthBackend from "./RsoAuthBackend";
import {GetSmartLoginFlow, rsoSmartLogin} from "./RsoAuthBackend";
import FastLoginForm from "./RsoFastLoginForm";
import {handleNextStep} from "./RsoHandleNextStep";
import DynamicForm from "./RsoDynamicForm";
import CustomGithubCorner from "../common/CustomGithubCorner";
// import LanguageSelect from "../common/select/LanguageSelect";
import i18next from "i18next";
import rsoLogo from "../assets/images/logo.png";
import * as OrganizationBackend from "../backend/OrganizationBackend";
import {getProtocolType} from "../utils/AuthUtil";
import RsoSmartFastLoginForm from "./RsoSmartFastLoginForm";
import * as Util from "./Util";
import * as AuthBackend from "./AuthBackend";
import {ArrowLeftOutlined, ArrowRightOutlined} from "@ant-design/icons";

const {Footer} = Layout;

const FORM_STATES = {
  FETCHING_FLOW: "fetching_flow",
  DYNAMIC_FORM: "dynamic_form",
  SUCCESS: "success",
};

class RsoSmartLoginPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      identifier: "",
      currentStep: FORM_STATES.FETCHING_FLOW,
      isFirstStep: true,
      state_id: "",
      formComponent: null,
      shouldReloadCaptcha: 0,
      responseCurrentStep: null,
      stepProviders: [],
      actions: [],
      delegatedAccounts: [],
      loading: false,
      username: "",
      loginContext: {
        application: "",
        organization: "",
      },
      enableForeignRegister: null,
      serviceUrl: "",
      account: null,
      loadingAccount: true,
      detectedType: "",
      mfaType: "",
    };
    this.formRef = React.createRef();
  }

  waitForApplication = (onReady, maxRetries = 20, retryDelay = 100) => {
    let attempts = 0;
    const interval = setInterval(() => {
      if (this.props.application || attempts >= maxRetries) {
        clearInterval(interval);
        onReady(this.props.application);
      } else {
        attempts++;
      }
    }, retryDelay);
  };

  async componentDidMount() {
    await this.fetchAccount();

    if (!sessionStorage.getItem("userToggledForeign")) {
      localStorage.removeItem("is_foreigner");
    }
    sessionStorage.removeItem("userToggledForeign");

    this.waitForApplication(async(application) => {
      if (!application) {
        showMessage("error", "Application data failed to load.");
        return;
      }

      try {
        const res = await OrganizationBackend.getEnableForeignRegister("admin", application.organization);
        this.setState({enableForeignRegister: res.data});
      } catch (err) {
        console.error("Error fetching enableForeignRegister:", err);
      }

      const storedOAuthData = localStorage.getItem("rso_oauth_callback_data");
      const ldapStateId = localStorage.getItem("ldap_state_id");
      localStorage.removeItem("ldap_state_id");
      let oauthData = null;

      if (storedOAuthData) {
        try {
          oauthData = JSON.parse(storedOAuthData);
        } catch (e) {
          console.error("Failed to parse oauth callback data", e);
        }
        localStorage.removeItem("rso_oauth_callback_data");
      }
      if (oauthData && oauthData.code) {
        await this.handleOAuthCallbackLogin(application, oauthData);
      } else if (ldapStateId) {
        await this.handleLdapLogin(application, ldapStateId);
      } else {
        await this.fetchInitialFlow(application);
      }
    });
  }

  handleLdapLogin = async(application, stateId) => {
    this.setState({loading: true, currentStep: FORM_STATES.DYNAMIC_FORM});

    try {

      const stepData = {};

      const protocolType = getProtocolType(this.props.location, this.props.application);
      const innerParams = this.getInnerParams();
      const oauthParams = Util.getOAuthGetParameters(innerParams);
      const serviceUrl = innerParams.get("service") || "";
      this.setState({serviceUrl: serviceUrl});
      const response = await RsoAuthBackend.rsoSmartLogin(
        stepData,
        {},
        stateId,
        application.name,
        application.organization,
        protocolType,
        oauthParams,
        serviceUrl
      );

      this.setState({
        responseCurrentStep: response?.data?.current_step,
      });

      if (response?.status === "ok") {
        this.setState({state_id: response.data.state_id});
        await handleNextStep(
          response,
          application.name,
          application.organization,
          this.state.username || "",
          (formConfig) => {
            this.setState({
              formComponent: {...formConfig},
              isFirstStep: false,
            });
          },
          this.props.application,
          this.props.location,
          oauthParams,
          this.state.serviceUrl
        );
      } else {
        throw new Error(response?.msg || "OAuth operation failed");
      }
    } catch (error) {
      showMessage("error", error.message || i18next.t("general:Error submitting form"));
      await this.fetchInitialFlow(application);
    } finally {
      this.setState({loading: false});
    }
  };
  handleOAuthCallbackLogin = async(application, oauthData) => {
    this.setState({loading: true, currentStep: FORM_STATES.DYNAMIC_FORM});

    try {
      const stateId = localStorage.getItem("rso_smart_login_state_id") || "";
      const selectedProvider = localStorage.getItem("rso_selected_provider") || oauthData.provider;

      localStorage.removeItem("rso_smart_login_state_id");
      localStorage.removeItem("rso_selected_provider");

      const stepData = {
        selected_provider: selectedProvider,
        oauth_provider: "",
      };

      const protocolType = getProtocolType(this.props.location, this.props.application);
      const innerParams = this.getInnerParams();
      const oauthParams = Util.getOAuthGetParameters(innerParams);

      const serviceUrl = innerParams.get("service") || "";
      this.setState({serviceUrl: serviceUrl});

      const response = await RsoAuthBackend.rsoSmartLogin(
        stepData,
        {},
        stateId,
        application.name,
        application.organization,
        protocolType,
        oauthParams,
        serviceUrl,
        "",
        oauthData.signinMethod,
        oauthData.code,
        oauthData.state,
        oauthData.redirectUri,
        oauthData.method,
        oauthData.codeVerifier,
        oauthData.samlRequest,
        oauthData.provider
      );

      this.setState({
        responseCurrentStep: response?.data?.current_step,
      });

      if (response?.status === "ok") {
        this.setState({state_id: response.data.state_id});
        await handleNextStep(
          response,
          application.name,
          application.organization,
          this.state.username || "",
          (formConfig) => {
            this.setState({
              formComponent: {...formConfig},
              isFirstStep: false,
            });
          },
          this.props.application,
          this.props.location,
          oauthParams,
          this.state.serviceUrl
        );
      } else {
        throw new Error(response?.msg || "OAuth operation failed");
      }
    } catch (error) {
      showMessage("error", error.message || i18next.t("general:Error submitting form"));
      await this.fetchInitialFlow(application);
    } finally {
      this.setState({loading: false});
    }
  };

  fetchInitialFlow = async(application) => {
    this.setState({loading: true});
    try {

      const flowRes = await GetSmartLoginFlow(application.organization, application.name);

      let steps = [];
      if (Array.isArray(flowRes)) {
        steps = flowRes;
      } else if (flowRes && Array.isArray(flowRes.steps)) {
        steps = flowRes.steps;
      } else if (flowRes && Array.isArray(flowRes.data)) {
        steps = flowRes.data;
      }

      if (steps && steps.length > 0) {

        const firstStep = steps.find(step => step.enabled !== false);

        if (!firstStep) {
          throw new Error(i18next.t("general:No enabled steps found in the login flow."));
        }

        const formConfig = {
          stepName: firstStep.name,
          dataRequired: {},
          initialValues: {},
          isNextStepSkippable: firstStep.is_next_step_skippable || false,
          onSubmit: async(values) => await this.executeFirstLoginStep(values),
        };

        if (firstStep.data_required && Array.isArray(firstStep.data_required)) {
          firstStep.data_required.forEach(field => {
            formConfig.dataRequired[field.name] = {...field};
          });
        }

        this.setState({
          currentStep: FORM_STATES.DYNAMIC_FORM,
          formComponent: formConfig,
          isFirstStep: true,
        });
      } else {
        throw new Error(i18next.t("general:Login flow configuration is empty."));
      }
    } catch (error) {
      console.error("Error fetching initial flow:", error);
      showMessage("error", error.message || i18next.t("general:Failed to load login flow"));
    } finally {
      this.setState({loading: false});
    }
  };

  executeFirstLoginStep = async(values) => {
    const {step_action, ...restValues} = values;

    const {identifier, username, phone, nationalId} = restValues;
    const mainIdentifier = identifier || username || phone || nationalId || Object.values(restValues)[0];

    if (mainIdentifier) {
      localStorage.setItem("identifier", mainIdentifier);

      let detectedType = "username";
      if (/^09\d{9}$/.test(mainIdentifier)) {
        detectedType = "phone";
      } else if (/^[\d\u06F0-\u06F9]{10}$/.test(mainIdentifier)) {
        detectedType = "nationalId";
      }
      this.setState({username: mainIdentifier, detectedType: detectedType});
    }

    const application = this.getApplicationObj();
    const payload = {...restValues}; // 2. استپ دیتا الان تمیز و بدون اکشن است

    if (this.hasActiveSession()) {
      payload.existingSession = true;
      payload.sessionUser = this.state.account.name;
    }

    const protocolType = getProtocolType(this.props.location, this.props.application);
    const innerParams = this.getInnerParams();
    const oauthParams = Util.getOAuthGetParameters(innerParams);

    let serviceUrl = "";
    if (protocolType === "cas") {
      serviceUrl = innerParams.get("service") || "";
      this.setState({serviceUrl});
    }

    // 3. اضافه کردن اکشن به additionalData
    const additionalData = {};
    if (step_action) {
      additionalData.step_action = step_action;
    }

    return await RsoAuthBackend.rsoSmartLogin(
      payload,
      additionalData,
      "",
      application.name,
      application.organization,
      protocolType,
      oauthParams,
      serviceUrl
    );
  };

  isProviderVisible(providerItem) {
    if (this.state.mode === "signup") {
      return isProviderVisibleForSignUp(providerItem);
    } else {
      return isProviderVisibleForSignIn(providerItem);
    }
  }

  renderOtherFormProvider(application) {
    if (inIframe()) {
      return null;
    }

    for (const providerConf of application.providers) {
      if (providerConf.provider?.type === "Google" && providerConf.rule === "OneTap" && this.props.preview !== "auto") {
        return (
          <GoogleOneTapLoginVirtualButton application={application} providerConf={providerConf} />
        );
      }
    }

    return null;
  }

  getApplicationObj() {
    const {application} = this.props;
    if (application) {
      return application;
    }
    return {
      name: "app-built-in",
      organization: "built-in",
      organizationObj: {
        languages: ["en", "fa"],
        countryCodes: ["+98", "+971"],
      },
      formCss: "",
      formCssMobile: "",
      formSideHtml: "",
      formOffset: 0,
    };
  }

  getInnerParams() {
    const params = new URLSearchParams(this.props.location.search);
    return new URLSearchParams(params);
  }

  incrementReloadCaptcha = () => {
    this.setState(prevState => ({
      shouldReloadCaptcha: prevState.shouldReloadCaptcha + 1,
    }));
  };

  handleMfaVerify = async(formData, stateId) => {
    const applicationObj = this.getApplicationObj();
    const oauthParams = Util.getOAuthGetParameters();
    const protocolType = getProtocolType(this.props.location, this.props.application);

    const mfaData = {
      stepData: formData,
      stateId: stateId,
      application: applicationObj.name,
      organization: applicationObj.organization,
      username: this.state.username,
      protocolType: protocolType,
      type: protocolType,
      oauthParams: oauthParams,
    };

    const response = await fetch(`${ServerUrl}/api/smart-login-mfa-verify`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "Accept-Language": getAcceptLanguage(),
      },
      body: JSON.stringify(mfaData),
    });

    if (!response.ok) {
      const errorMessage = `${i18next.t("mfa:MFA verify failed")}: ${response.status}`;
      throw new Error(errorMessage);
    }

    return await response.json();
  };

  executeGenericNextStep = (formData, stateId) => {
    const application = this.getApplicationObj();
    const protocolType = getProtocolType(this.props.location, this.props.application);
    const oauthParams = Util.getOAuthGetParameters();
    const innerParams = this.getInnerParams();
    const serviceUrl = innerParams.get("service") || "";

    return RsoAuthBackend.rsoSmartLogin(
      formData,
      {},
      stateId,
      application.name,
      application.organization,
      protocolType,
      oauthParams,
      serviceUrl
    );
  };

  handleSubmitDynamicForm = async(formData) => {
    this.setState({loading: true});
    if (this.state.responseCurrentStep === "verify_mfa") {
      try {
        const mfaResponse = await this.handleMfaVerify({
          ...formData,
          mfa_type: this.state.mfaType,
          mfaTriggeredType: this.state.mfaType,
        }, this.state.state_id);
        const oauthParams = Util.getOAuthGetParameters();
        if (mfaResponse.status === "ok") {
          const applicationObj = this.getApplicationObj();
          await handleNextStep(
            mfaResponse,
            applicationObj.name,
            applicationObj.organization,
            this.state.username,
            (formConfig) => {
              this.setState({
                formComponent: {...formConfig},
                isFirstStep: false,
              });
            },
            this.props.application,
            this.props.location,
            oauthParams,
            this.state.serviceUrl
          );
        }
        return;
      } catch (error) {
        this.incrementReloadCaptcha();
        showMessage("error", error.message || i18next.t("general:Error submitting form"));
        return;
      } finally {
        this.setState({loading: false});
      }
    }

    try {
      const response = await this.state.formComponent.onSubmit(formData);
      const oauthParams = Util.getOAuthGetParameters();
      console.log("response?.data?.actions", response?.data?.actions);
      this.setState({
        responseCurrentStep: response?.data?.current_step,
        actions: response?.data?.actions ?? response?.data?.actions,
        mfaType: response?.data?.user_data?.mfaTriggeredType ?? response?.data?.user_data?.mfaTriggeredType,
        delegatedAccounts: response?.data?.delegatedAccounts ?? response?.data?.delegatedAccounts,
        stepProviders: response?.data?.step_providers ?? response?.data?.step_providers,
      });

      if (response?.status === "ok" || response?.status === "error") {
        if (response?.status === "ok") {
          this.setState({state_id: response.data.state_id});
        } else {
          this.incrementReloadCaptcha();
        }

        const applicationObj = this.getApplicationObj();

        await handleNextStep(
          response,
          applicationObj.name,
          applicationObj.organization,
          this.state.username,
          (formConfig) => {
            this.setState({
              formComponent: {...formConfig},
              isFirstStep: false,
            });
          },
          this.props.application,
          this.props.location,
          oauthParams,
          this.state.serviceUrl
        );
      } else {
        this.incrementReloadCaptcha();
        showMessage("error", response?.msg || "Operation failed");
      }
    } catch (error) {
      this.incrementReloadCaptcha();
      showMessage("error", error?.message || "Operation failed");
    } finally {
      this.setState({loading: false});
    }
  };

  handleClickDelegatedAccounts = async(accountName) => {
    const applicationObj = this.getApplicationObj();
    const protocolType = getProtocolType(this.props.location, this.props.application);
    const oauthParams = Util.getOAuthGetParameters();
    const innerParams = this.getInnerParams();
    const serviceUrl = innerParams.get("service");
    const res = await rsoSmartLogin(
      {
        identifier: localStorage.getItem("identifier"),
      },
      {
        username: this.state.username,
        application: applicationObj.name,
        organization: applicationObj.organization,
      },
      "",
      applicationObj.name,
      applicationObj.organization,
      protocolType,
      oauthParams,
      serviceUrl,
      accountName
    );
    if (res?.status === "ok") {
      switch (protocolType) {
      case "cas": {
        const st = res.data.user_data.data;
        let redirectTarget = "/";
        if (res.data.user_data.data2 !== null && res.data.user_data.data2 !== "") {
          redirectTarget = res.data.user_data.data2;
          const newUrl = new URL(redirectTarget);
          newUrl.searchParams.append("ticket", st);
          console.log("CAS redirect", newUrl.toString());
          window.location.href = newUrl.toString();
          return;
        }
        break;
      }

      case "code": {
        const code = res.data.user_data.data;
        const concatChar = oauthParams?.redirectUri?.includes("?") ? "&" : "?";
        const clientId = oauthParams?.clientId;
        window.location.href = `${oauthParams.redirectUri}${concatChar}client_id=${clientId}&code=${code}&state=${oauthParams.state}`;
        return;
      }
      }

      window.location.href = "/";
    }
  };

  renderProviders = (application) => {
    const {formComponent} = this.state;

    if (Object.entries(formComponent?.dataRequired || {}).length > 1) {
      return;
    }

    if (!application?.providers) {
      return null;
    }
    const searchParams = new URLSearchParams(window.location.search);
    const providerHint = searchParams.get("provider_hint");
    return (
      <Form.Item style={{marginBottom: 0}}>
        {
          application.providers.filter(providerItem => this.isProviderVisible(providerItem)).map((providerItem, id) => {
            if (providerHint === providerItem.provider.name) {
              goToLink(Provider.getAuthUrl(application, providerItem.provider, "signup"));
              return;
            }
            return (
              <span key={id} onClick={(e) => {
                const agreementChecked = this.form?.current.getFieldValue("agreement");

                if (agreementChecked !== undefined && typeof agreementChecked === "boolean" && !agreementChecked) {
                  e.preventDefault();
                  message.error(i18next.t("signup:Please accept the agreement!"));
                }
              }}>
                {
                  ProviderButton.renderProviderLogo(providerItem.provider, application, null, null, "Always", this.props.location)
                }
              </span>
            );
          })
        }
        {
          this.renderOtherFormProvider(application)
        }
      </Form.Item>
    );
  };

  fetchAccount = async() => {
    try {
      const res = await AuthBackend.getAccount();
      if (res.status === "ok") {
        this.setState({
          account: res.data,
          loadingAccount: false,
        });
      } else {
        this.setState({
          account: null,
          loadingAccount: false,
        });
      }
    } catch (error) {
      console.error("❌ Error fetching account:", error);
      this.setState({
        account: null,
        loadingAccount: false,
      });
    }
  };

  hasActiveSession() {
    const account = this.props.account || this.state.account;
    return account !== undefined && account !== null && account !== "";
  }

  canAccessApplicationViaSSO() {
    const account = this.props.account || this.state.account;

    if (!this.hasActiveSession()) {
      return false;
    }

    const application = this.getApplicationObj();
    return application && account && application.organization === account.owner;
  }

  renderSignedInBox() {
    if (this.state.loadingAccount) {
      return (
        <div style={{textAlign: "center", padding: "20px"}}>
          <Spin size="small" />
          <div style={{marginTop: "10px", color: "#666"}}>Checking sessions...</div>
        </div>
      );
    }

    if (!this.canAccessApplicationViaSSO()) {
      return null;
    }

    const account = this.state.account;

    if (!this.state.isFirstStep) {
      return null;
    }

    return (
      <div style={{marginBottom: "20px", border: "2px solid #1890ff", padding: "16px", borderRadius: "8px"}}>
        <div style={{
          fontSize: 16,
          textAlign: i18next.language === "fa" ? "right" : "left",
          marginBottom: "10px",
          color: "#1890ff",
          fontWeight: "bold",
        }}>
          🚀 {i18next.t("login:Continue with")}:
        </div>

        <div
          style={{
            border: "2px solid #1890ff",
            borderRadius: "6px",
            padding: "12px 16px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginBottom: "16px",
            backgroundColor: "#f0f8ff",
            transition: "all 0.3s",
          }}
          onClick={this.handleQuickLogin}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#e6f7ff";
            e.currentTarget.style.borderColor = "#40a9ff";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#f0f8ff";
            e.currentTarget.style.borderColor = "#1890ff";
          }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              backgroundColor: "#1890ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontWeight: "bold",
              fontSize: "16px",
            }}
          >
            {account.displayName ? account.displayName.charAt(0).toUpperCase() : account.name.charAt(0).toUpperCase()}
          </div>
          <div style={{flex: 1}}>
            <div style={{fontWeight: "bold", fontSize: "16px", color: "#1890ff"}}>
              {account.displayName || account.name}
            </div>
            <div style={{fontSize: "14px", color: "#666"}}>
              {account.email || account.phone || account.name}
            </div>
          </div>
          <div style={{color: "#1890ff", fontSize: "14px", fontWeight: "bold", display: "flex", gap: 4}}>
            {i18next.t("login:Click to continue")} {i18next.language === "fa" ? <ArrowLeftOutlined /> :
              <ArrowRightOutlined />}
          </div>
        </div>

        <div style={{
          fontSize: 14,
          textAlign: "center",
          color: "#666",
          marginBottom: "16px",
          borderTop: "1px solid #eee",
          paddingTop: "16px",
        }}>
          {i18next.t("login:Or sign in with another account")}
        </div>
      </div>
    );
  }

  handleQuickLogin = async() => {
    const application = this.getApplicationObj();
    const account = this.state.account;

    if (!application || !account) {
      console.error("❌ Missing application or account for quick login");
      return;
    }

    const values = {
      identifier: account.name,
      existingSession: true,
      quickLogin: true,
    };

    try {
      this.setState({loading: true});
      const response = await this.executeFirstLoginStep(values);
      const oauthParams = Util.getOAuthGetParameters();

      if (response?.status === "ok") {
        this.setState({state_id: response.data.state_id});
        await handleNextStep(
          response,
          application.name,
          application.organization,
          account.name,
          (formConfig) => {
            this.setState({
              formComponent: {...formConfig},
              isFirstStep: false,
            });
          },
          this.props.application,
          this.props.location,
          oauthParams,
          this.state.serviceUrl
        );
      }
    } catch (error) {
      showMessage("error", error.message || i18next.t("general:Error in quick login flow"));
    } finally {
      this.setState({loading: false});
    }
  };

  renderExtraFirstStepLinks = (application, isFastLogin, isForeigner, enableForeignRegister) => {
    return (
      <div style={{marginTop: "15px"}}>
        {application?.rsoFastLogin === true && (
          <div style={{textAlign: "right", marginBottom: "8px"}}>
            {!isFastLogin ? (
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  localStorage.setItem("fastLogin", "true");
                  window.location.reload();
                }}
              >
                {i18next.t("general:Fast Login")}
              </a>
            ) : (
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  localStorage.removeItem("fastLogin");
                  window.location.reload();
                }}
              >
                {i18next.t("general:Login/SignUp")}
              </a>
            )}
          </div>
        )}

        {/* <div style={{*/}
        {/*  display: "flex",*/}
        {/*  justifyContent: "flex-start",*/}
        {/*  textAlign: "right",*/}
        {/*  width: "100%",*/}
        {/*  marginBottom: "8px",*/}
        {/* }}>*/}
        {/*  {React.cloneElement(*/}
        {/*    renderForgetLink(application, i18next.t("login:Forgot password?")),*/}
        {/*    {*/}
        {/*      onClick: (e) => {*/}
        {/*        const identifier = localStorage.getItem("identifier") || this.state.username;*/}
        {/*        if (!identifier || identifier.trim() === "") {*/}

        {/*          message.warning(i18next.t("login:Please provide an identifier during the process."));*/}
        {/*        } else {*/}
        {/*          sessionStorage.setItem("forgetIdentifier", identifier);*/}
        {/*        }*/}
        {/*      },*/}
        {/*    }*/}
        {/*  )}*/}
        {/* </div>*/}

        {enableForeignRegister ? (
          <div style={{textAlign: "right"}}>
            {!isForeigner ? (
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  sessionStorage.setItem("userToggledForeign", "true");
                  localStorage.setItem("is_foreigner", "true");
                  window.location.reload();
                }}
              >
                {i18next.t("general:Foreign Login")}
              </a>
            ) : (
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  sessionStorage.setItem("userToggledForeign", "true");
                  localStorage.removeItem("is_foreigner");
                  window.location.reload();
                }}
              >
                {i18next.t("general:Iranian Login")}
              </a>
            )}
          </div>
        ) : null}
      </div>
    );
  };

  renderLoginForm = () => {
    const {currentStep, formComponent, loading, enableForeignRegister, delegatedAccounts, isFirstStep} = this.state;

    const params = new URLSearchParams(window.location.search);
    const isSmartFastLogin = params.get("smart_fast_login") === "1";
    const application = this.getApplicationObj();
    const isFastLogin = localStorage.getItem("fastLogin") === "true";
    const isForeigner = localStorage.getItem("is_foreigner") === "true";
    if (isSmartFastLogin || application?.rsoSmartFastLogin) {
      return (
        <RsoSmartFastLoginForm
          application={this.getApplicationObj()}
          loading={this.state.loading}
          shouldReloadCaptcha={this.state.shouldReloadCaptcha}
          onSubmit={this.executeFirstLoginStep}
        />
      );
    }

    if (delegatedAccounts?.length > 0) {
      return (
        <>
          <Flex gap={12} align="center" justify="center" vertical style={{width: "100%"}}>
            <div style={{fontSize: "16px", fontWeight: "bold"}}>
              {i18next.t("general:delegation_select")}
            </div>
            <div className="delegated" onClick={() => window.location.href = "/"} style={{
              cursor: "pointer",
              padding: "12px",
              borderRadius: "8px",
              border: "1px solid #e3e3e3",
              backgroundColor: "rgba(255,255,255,0.11)",
              width: "100%",
            }}>
              <Flex align="center" justify="space-between">
                <div style={{color: "white"}}>
                  {localStorage.getItem("identifier")}
                </div>
                <ArrowLeftOutlined style={{fontSize: "18px", color: "white"}} />
              </Flex>
            </div>
          </Flex>
          <Divider style={{margin: "16px 0"}} />
          <Flex gap={12} align="center" justify="center" vertical style={{width: "100%"}}>
            {delegatedAccounts.map((o) => (
              <div className="delegated" onClick={() => this.handleClickDelegatedAccounts(o)} key={o} style={{
                cursor: "pointer",
                padding: "12px",
                borderRadius: "8px",
                border: "1px solid #e3e3e3",
                backgroundColor: "rgba(255,255,255,0.11)",
                width: "100%",
              }}>
                <Flex align="center" justify="space-between">
                  <div style={{color: "white"}}>
                    {o}
                  </div>
                  <ArrowLeftOutlined style={{fontSize: "18px", color: "white"}} />
                </Flex>
              </div>
            ))}
          </Flex>
        </>
      );
    }

    if (currentStep === FORM_STATES.FETCHING_FLOW) {
      return (
        <div style={{textAlign: "center", padding: "40px"}}>
          <Spin size="large" tip={i18next.t("general:Loading login flow...")} />
        </div>
      );
    }

    if (currentStep === FORM_STATES.DYNAMIC_FORM && formComponent) {
      if (isFirstStep && isFastLogin) {
        return (
          <>
            {this.renderSignedInBox()}
            <FastLoginForm
              application={this.getApplicationObj()}
              loading={loading}
              shouldReloadCaptcha={this.state.shouldReloadCaptcha}
              onSubmit={async(values) => {
                this.setState({loading: true});
                const response = await this.executeFirstLoginStep(values);
                await this.handleSubmitDynamicForm(response);
              }}
            />
          </>
        );
      }

      return (
        <>
          {this.renderSignedInBox()}

          {isFirstStep && isForeigner && (
            <div style={{textAlign: "center", marginBottom: "16px", fontSize: "18px", fontWeight: "bold"}}>
              {i18next.t("general:Foreign Authentication")}
            </div>
          )}

          <DynamicForm
            account={this.state.account || this.props.account}
            isForeigner={isForeigner}
            shouldReloadCaptcha={this.state.shouldReloadCaptcha}
            stepProviders={this.state.stepProviders}
            actions={this.state.actions}
            stateId={this.state.state_id}
            location={this.props.location}
            mfaType={this.state.mfaType}
            serviceUrl={this.state.serviceUrl}
            returnUrl={this.state.returnUrl}
            application={application}
            key={JSON.stringify(formComponent)}
            currentStep={this.state.currentStep}
            responseCurrentStep={this.state.responseCurrentStep}
            formConfig={{
              ...formComponent,
              onSubmit: this.handleSubmitDynamicForm,
            }}
            onFormConfigChange={this.handleGoBackUpdate}
            onStepStateChange={(isCurrentlyFirstStep) => {
              if (this.state.isFirstStep !== isCurrentlyFirstStep) {
                this.setState({isFirstStep: isCurrentlyFirstStep});
              }
            }}
          />

          {isFirstStep && this.renderExtraFirstStepLinks(application, isFastLogin, isForeigner, enableForeignRegister)}
        </>
      );
    }

    if (currentStep === FORM_STATES.SUCCESS) {
      return (
        <Result
          status="success"
          title={i18next.t("general:Successful Login")}
          subTitle={i18next.t("general:You have successfully logged in.")}
          extra={[
            <Button key="homepage" type="primary" onClick={() => (window.location.href = "/")}>
              {i18next.t("general:Login to homepage")}
            </Button>,
          ]}
        />
      );
    }

    return null;
  };

  parseOffset = (offset) => {
    if (offset === 2 || offset === 4 || inIframe() || isMobile()) {
      return "0 auto";
    }
    if (offset === 1) {
      return "0 10%";
    }
    if (offset === 3) {
      return "0 60%";
    }
    return "0 auto";
  };

  handleGoBackUpdate = (previousFormConfig) => {
    const isInitial = previousFormConfig.isInitialStep;

    let submitHandler;
    if (isInitial) {
      submitHandler = this.executeFirstLoginStep;
    } else {
      submitHandler = (formData) => this.executeGenericNextStep(formData, previousFormConfig.stateId);
    }

    const finalConfig = {
      ...previousFormConfig,
      onSubmit: submitHandler,
    };

    this.setState({
      formComponent: finalConfig,
      responseCurrentStep: finalConfig.stepName,
      state_id: finalConfig.stateId,
      isFirstStep: isInitial,
    });
  };

  renderPageFooter() {
    const footerHtml = this.props.application?.footerHtml ?? this.props.application?.organizationObj?.footerHtml;
    return (
      <React.Fragment>
        <Footer id="footer" style={{textAlign: "center"}}>
          {
            footerHtml && footerHtml !== "" ?
              <React.Fragment>
                <div dangerouslySetInnerHTML={{__html: footerHtml}} />
              </React.Fragment>
              : (
                <React.Fragment>
                  {i18next.t("general:powered by")} <a target="_blank" href="https://rso-co.ir/" rel="noreferrer"><img
                    height={"50px"} alt={"Casdoor"} src={rsoLogo} /></a>
                </React.Fragment>
              )
          }
        </Footer>
      </React.Fragment>
    );
  }

  render() {
    const application = this.getApplicationObj();
    if (!this.props.application) {
      return (
        <div style={{
          width: "100vw",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}>
          <Spin size="large" tip={i18next.t("general:Loading application...")} />
        </div>
      );
    }

    return (
      <React.Fragment>
        <CustomGithubCorner />

        <div
          className="login-content"
          style={{
            margin: this.parseOffset(
              application.formOffset ?? application.organizationObj.formOffset
            ),
          }}
        >

          {!inIframe() && !isMobile() && (
            <div
              dangerouslySetInnerHTML={{
                __html: application.formCss ?? application.organizationObj.formCss,
              }}
            />
          )}

          {(inIframe() || isMobile()) && (
            <div
              dangerouslySetInnerHTML={{
                __html: application.formCssMobile ?? application.organizationObj.formCssMobile,
              }}
            />
          )}

          <div className={isDarkTheme() ? "login-panel-dark" : "login-panel"}>

            <div
              className="side-image"
              style={{
                display:
                  (application.formOffset ?? application.organizationObj.formOffset) !== 4
                    ? "none"
                    : null,
              }}
            >
              <div
                dangerouslySetInnerHTML={{
                  __html:
                    application.formSideHtml ??
                    application.organizationObj.formSideHtml,
                }}
              />
            </div>

            <div className="login-form">
              {renderHelmet(application)}
              {renderLogo(application)}

              {/* <LanguageSelect */}
              {/*   languages={ */}
              {/*     application.languages ?? */}
              {/*     application.organizationObj.languages */}
              {/*   } */}
              {/*   style={{top: "0", right: "0", position: "absolute"}} */}
              {/* /> */}

              <Spin spinning={this.state.loading}>
                {this.renderLoginForm()}
                {this.renderProviders(application)}
              </Spin>
            </div>
          </div>

          {this.renderPageFooter()}
        </div>
      </React.Fragment>
    );
  }
}

export default RsoSmartLoginPage;
